import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { serviceLabel } from "@/components/booking/constants";
import SignaturePad from "@/components/SignaturePad";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import OrderAttachments from "@/components/OrderAttachments";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Package, Truck, CheckCircle2, PackageCheck, CalendarClock, FilePlus2, ClipboardList, Route, ImagePlus, CornerUpRight, Printer, LayoutGrid, FileText, Camera, Paperclip, ArrowRightLeft, ExternalLink, Building2 } from "lucide-react";
import { toast } from "sonner";
import EditOrderDialog from "@/components/EditOrderDialog";
import PaintRunCard from "@/components/driver/PaintRunCard";
import { PickupDialog, TransferDialog } from "@/components/driver/JobActions";
import JobLeg from "@/components/driver/JobLeg";

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sheetBusy, setSheetBusy] = useState(false);

  const printRunSheet = async () => {
    setSheetBusy(true);
    try {
      const res = await api.get("/driver/run-sheet", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "run-sheet.pdf";
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not generate the run sheet");
    } finally {
      setSheetBusy(false);
    }
  };

  const [offline, setOffline] = useState(!navigator.onLine);

  const knownIds = useRef(null);
  const load = useCallback(() => api.get("/driver/jobs").then((r) => {
    const fresh = r.data;
    if (knownIds.current) {
      const added = fresh.filter((j) => !j.transferred_away && j.status === "assigned" && !knownIds.current.has(j.id));
      if (added.length) toast.success(added.length === 1 ? `New job on your run: ${added[0].tracking_number} (${added[0].pickup?.city} → ${added[0].dropoff?.city})` : `${added.length} new jobs added to your run`, { duration: 8000 });
    }
    knownIds.current = new Set(fresh.map((j) => j.id));
    setJobs(fresh);
  }).catch(() => setJobs((prev) => prev || [])), []);
  useEffect(() => { load(); }, [load]);

  // Keep the list live: Dave drags jobs onto runs during the day — poll + refresh whenever the phone wakes up
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible" && navigator.onLine) load(); };
    const t = setInterval(tick, 45000);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", tick); window.removeEventListener("focus", tick); };
  }, [load]);

  useEffect(() => {
    api.get("/driver/run-sheet", { responseType: "blob" }).catch(() => {});
    const on = () => { setOffline(false); load(); };
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, [load]);

  useEffect(() => {
    const ping = async () => {
      const p = await getPos();
      if (p.lat != null) api.post("/driver/location", p).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 60000);
    return () => clearInterval(t);
  }, []);

  const CLOSED = ["delivered", "onforwarded", "cancelled"];
  const shells = jobs?.filter((j) => j.transferred_away) || [];
  const mine = jobs?.filter((j) => !j.transferred_away) || [];
  const active = mine.filter((j) => !CLOSED.includes(j.status));
  const done = mine.filter((j) => CLOSED.includes(j.status));
  const todayStr = new Date().toLocaleDateString("en-CA");
  const tomorrowStr = new Date(Date.now() + 864e5).toLocaleDateString("en-CA");
  const dayKey = (j) => { const d = j.run_date || j.scheduled_date || ""; return !d || d <= todayStr ? todayStr : d; };
  const dayLabel = (k) => (k === todayStr ? "Today" : k === tomorrowStr ? "Tomorrow" : new Date(k + "T12:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" }));
  const byDay = {};
  active.forEach((j) => { const k = dayKey(j); (byDay[k] = byDay[k] || []).push(j); });
  const dayKeys = Object.keys(byDay).sort();
  const actionable = active.filter((j) => j.status === "assigned" && dayKey(j) === todayStr);
  const sel = (st) => actionable.filter((j) => j.status === st && selected.has(j.id)).map((j) => j.id);
  const selAssigned = sel("assigned");
  const selCount = selAssigned.length;

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = actionable.length > 0 && selCount === actionable.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(actionable.map((j) => j.id)));

  const bulk = async (ids, status, label) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post("/driver/jobs/bulk-status", { order_ids: ids, status });
      toast.success(`${label} ${data.count} job${data.count === 1 ? "" : "s"} — customers notified`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBulkBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-8 pb-28 sm:px-6">
        {offline && (
          <div data-testid="offline-banner" className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
            You're offline — showing your last saved jobs and run sheet. Status updates will need reception.
          </div>
        )}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Your jobs</h1>
            <p className="mt-1 text-sm text-muted-foreground">Update status as you go — customers are notified automatically.</p>
          </div>
          <div className="flex shrink-0 flex-col gap-2">
            <Button onClick={() => navigate("/quotes/new")} data-testid="driver-new-quote-button" className="w-full justify-start gap-1.5 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
              <FilePlus2 className="h-4 w-4" /> New quote
            </Button>
            <Button variant="outline" onClick={() => navigate("/manifest")} data-testid="driver-manifest-button" className="w-full justify-start gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              <ClipboardList className="h-4 w-4" /> Manifest
            </Button>
            <Button variant="outline" onClick={() => navigate("/driver/route")} data-testid="driver-route-button" className="w-full justify-start gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              <Route className="h-4 w-4" /> Route
            </Button>
            <Button variant="outline" onClick={printRunSheet} disabled={sheetBusy} data-testid="driver-run-sheet-button" className="w-full justify-start gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              {sheetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />} Run sheet
            </Button>
            <Button variant="outline" onClick={() => navigate("/driver/board")} data-testid="driver-board-button" className="w-full justify-start gap-1.5 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
              <LayoutGrid className="h-4 w-4" /> Board
            </Button>
          </div>
        </div>

        {jobs === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : mine.length === 0 && shells.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="no-jobs">
            <Truck className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No jobs assigned yet. Check back soon.</p>
          </div>
        ) : (
          <>
            {actionable.length > 1 && (
              <label className="mt-6 flex cursor-pointer items-center gap-2 rounded-md border bg-secondary/40 p-3" data-testid="bulk-select-bar">
                <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-actionable" />
                <span className="text-sm text-muted-foreground">Select all actionable ({actionable.length})</span>
              </label>
            )}
            <div className="mt-6 space-y-8" data-testid="active-jobs">
              {dayKeys.map((day) => {
                const dayJobs = byDay[day];
                const lanes = [
                  { key: "pickup", label: "Ready for pickup", icon: Package, jobs: dayJobs.filter((j) => j.status === "assigned").sort((a, b) => (a.run_sequence ?? 999) - (b.run_sequence ?? 999)), testid: `lane-for-pickup-${day}` },
                  { key: "onboard", label: "On board", icon: Truck, jobs: dayJobs.filter((j) => ["picked_up", "in_transit"].includes(j.status)).sort((a, b) => (a.run_sequence ?? 999) - (b.run_sequence ?? 999)), testid: `lane-on-board-${day}` },
                ];
                return (
                  <section key={day} data-testid={`day-group-${day}`}>
                    <div className="mb-3 flex items-center gap-2 border-b border-kilted-green/20 pb-2">
                      <CalendarClock className="h-5 w-5 text-kilted-green" />
                      <h2 className="text-xl font-extrabold text-kilted-green" data-testid={`day-heading-${day}`}>{dayLabel(day)}</h2>
                      <span className="rounded-full bg-kilted-green/10 px-2.5 py-0.5 text-xs font-bold text-kilted-green">{dayJobs.length} job{dayJobs.length === 1 ? "" : "s"}</span>
                    </div>
                    {day === todayStr ? (
                      <div className="space-y-6">
                        {lanes.map((lane) => lane.jobs.length > 0 && (
                          <section key={lane.key} data-testid={lane.testid}>
                            <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-kilted-green">
                              <lane.icon className="h-4 w-4" /> {lane.label}
                              <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-semibold text-muted-foreground">{lane.jobs.length}</span>
                            </h3>
                            <div className="space-y-4">
                              {lane.jobs.map((j) => <JobCard key={j.id} job={j} onChange={load} selectable={j.status === "assigned"} checked={selected.has(j.id)} onToggle={() => toggle(j.id)} />)}
                            </div>
                          </section>
                        ))}
                        {dayJobs.filter((j) => !["assigned", "picked_up", "in_transit"].includes(j.status)).map((j) => (
                          <JobCard key={j.id} job={j} onChange={load} />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {dayJobs.sort((a, b) => (a.run_sequence ?? 999) - (b.run_sequence ?? 999)).map((j) => <JobCard key={j.id} job={j} onChange={load} upcoming />)}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
            {done.length > 0 && (
              <div className="mt-8" data-testid="lane-delivered">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-bold"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Delivered ({done.length})</h2>
                <div className="space-y-3" data-testid="done-jobs">{done.map((j) => <JobCard key={j.id} job={j} onChange={load} completed />)}</div>
              </div>
            )}
            {shells.length > 0 && (
              <div className="mt-8" data-testid="lane-transferred">
                <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-muted-foreground"><ArrowRightLeft className="h-5 w-5" /> Transferred to a colleague ({shells.length})</h2>
                <div className="space-y-3">{shells.map((j) => <TransferredShell key={j.id} job={j} />)}</div>
              </div>
            )}
          </>
        )}
        <PaintRunCard />
        <QuotesSection />
      </main>

      {selCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 p-4 backdrop-blur" data-testid="bulk-action-bar">
          <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
            <span className="text-sm font-medium">{selCount} selected</span>
            <div className="flex gap-2">
              {selAssigned.length > 0 && (
                <Button size="lg" onClick={() => bulk(selAssigned, "picked_up", "On board for delivery —")} disabled={bulkBusy} data-testid="bulk-pickup-button" className="h-12 gap-2 bg-kilted-green hover:bg-kilted-greenLight">
                  {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Package className="h-4 w-4" />} Picked up ({selAssigned.length})
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const getPos = () => new Promise((res) => {
  if (!navigator.geolocation) return res({});
  navigator.geolocation.getCurrentPosition(
    (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
    () => res({}), { timeout: 2500, maximumAge: 60000 });
});

function QuotesSection() {
  const [quotes, setQuotes] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/driver/quotes").then((r) => setQuotes(r.data)).catch(() => setQuotes([])), []);
  useEffect(() => { load(); }, [load]);
  if (!quotes?.length) return null;
  return (
    <div className="mt-8" data-testid="driver-quotes-section">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="driver-quotes-toggle"
        className="flex w-full items-center gap-2 text-lg font-bold text-kilted-green">
        <FileText className="h-5 w-5" /> Quotes awaiting payment ({quotes.length})
        <span className="ml-auto text-xs font-normal text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {quotes.map((q) => (
            <div key={q.id} className="flex flex-wrap items-center gap-2 rounded-md border bg-white p-3 text-sm" data-testid={`quote-row-${q.tracking_number}`}>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold">{q.tracking_number} · ${q.price?.total?.toFixed(2)}</p>
                <p className="truncate text-xs text-muted-foreground">{q.customer_name} — {q.pickup?.city} → {q.dropoff?.city}</p>
              </div>
              <EditOrderDialog order={q} onSaved={load} triggerClassName="h-8 gap-1 border-kilted-green px-2 text-xs text-kilted-green hover:bg-kilted-green/10" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JobCard({ job, onChange, completed, selectable, checked, onToggle, upcoming }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const photoRef = useRef(null);
  const galleryRef = useRef(null);

  const defer = async (days) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/jobs/${job.id}/defer`, { days });
      toast.success(`Deferred to ${new Date(data.run_date + "T12:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "short" })}`);
      onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const addPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("kind", "quality");
        await api.post(`/orders/${job.id}/photos`, fd);
      }
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} attached to the order`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't upload the photo"); }
    finally { if (photoRef.current) photoRef.current.value = ""; }
  };

  return (
    <Card data-testid={`job-card-${job.tracking_number}`} className={`animate-fade-in-up ${job.service === "sameday" ? "border-2 border-red-500" : job.service === "express" ? "border-2 border-orange-400" : ""}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          {selectable && <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`select-job-${job.tracking_number}`} />}
          <CardTitle className="font-heading text-lg">
            <button type="button" onClick={() => navigate(`/orders/${job.id}`)} data-testid={`open-job-${job.tracking_number}`}
              className="flex items-center gap-1 hover:text-kilted-gold hover:underline">{job.tracking_number} <ExternalLink className="h-3.5 w-3.5 opacity-60" /></button>
          </CardTitle>
          {job.delivery_run && (
            <span className="rounded-full bg-kilted-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-kilted-green" data-testid={`job-run-${job.tracking_number}`}>
              {job.run_sequence != null ? `Stop ${job.run_sequence + 1} · ` : ""}{job.delivery_run === "other" ? "Other run" : `${job.delivery_run.charAt(0).toUpperCase()}${job.delivery_run.slice(1)} run`}
            </span>
          )}
          {(job.service === "sameday" || job.service === "express") && (
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white ${job.service === "sameday" ? "bg-red-600" : "bg-orange-500"}`} data-testid={`job-priority-badge-${job.tracking_number}`}>
              {serviceLabel(job.service)}
            </span>
          )}
        </div>
        <StatusBadge status={job.status} />
      </CardHeader>
      <CardContent className="space-y-3">
        {job.service === "sameday" && (job.urgent_accepted_at ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" data-testid={`urgent-covered-${job.tracking_number}`}>
            <CheckCircle2 className="mr-1 inline h-4 w-4" /> Urgent job covered by {job.urgent_accepted_by}
          </p>
        ) : (
          <Button onClick={async () => {
            try { await api.post(`/driver/jobs/${job.id}/urgent-accept`); toast.success("Got it — Dave and the customer have been told"); onChange && onChange(); }
            catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
          }} className="h-12 w-full gap-2 bg-red-600 text-base font-bold text-white hover:bg-red-700" data-testid={`urgent-accept-${job.tracking_number}`}>
            <CheckCircle2 className="h-5 w-5" /> I've got it
          </Button>
        ))}
        <div className="space-y-2 rounded-md bg-secondary/50 p-3 text-sm">
          <JobLeg job={job} />
          <p className="text-xs text-muted-foreground"><Package className="mr-1 inline h-3 w-3" />{job.size} · {job.weight_kg}kg · {job.distance_km}km · {serviceLabel(job.service)}</p>
          {(job.scheduled_date || job.pickup_window) && (
            <p className="text-xs font-medium text-kilted-green" data-testid={`job-schedule-${job.tracking_number}`}>
              <CalendarClock className="mr-1 inline h-3 w-3" />Pickup: {[job.scheduled_date, job.pickup_window].filter(Boolean).join(" · ")}
            </p>
          )}
          {job.atl === true && <p className="text-xs font-semibold text-emerald-700" data-testid={`job-atl-${job.tracking_number}`}>✓ ATL — OK to leave in a safe place{job.atl_note ? <span className="font-normal text-emerald-800"> · Leave at: <b>{job.atl_note}</b></span> : ""}</p>}
          {job.atl === false && <p className="text-xs font-semibold text-red-600" data-testid={`job-noatl-${job.tracking_number}`}>✗ No ATL — signature required, do not leave unattended</p>}
          {job.delivery_instructions && <p className="text-xs text-muted-foreground" data-testid={`job-instructions-${job.tracking_number}`}>Instructions: {job.delivery_instructions}</p>}
          {job.directory_notes && Object.entries(job.directory_notes).map(([which, n]) => (
            <p key={which} className="rounded border border-kilted-gold/40 bg-kilted-gold/10 px-2 py-1 text-xs text-kilted-green" data-testid={`job-directory-${which}-${job.tracking_number}`}>
              <Building2 className="mr-1 inline h-3 w-3" /><b>{n.trade_name}</b> ({which === "pickup" ? "pickup" : "drop-off"}){n.instructions ? `: ${n.instructions}` : ""}
              {n.contact_phone && <> · <a href={`tel:${n.contact_phone.replace(/\s/g, "")}`} className="underline">{n.contact_name ? `${n.contact_name} ` : ""}{n.contact_phone}</a></>}
            </p>
          ))}
          {job.transfers?.length > 0 && (
            <p className="rounded bg-kilted-gold/15 px-2 py-1 text-xs font-medium text-kilted-green" data-testid={`job-transfer-note-${job.tracking_number}`}>
              <ArrowRightLeft className="mr-1 inline h-3 w-3" />Handed over by {job.transfers[job.transfers.length - 1].from_name}{job.transfers[job.transfers.length - 1].note ? ` — ${job.transfers[job.transfers.length - 1].note}` : ""}
            </p>
          )}
          {(job.attachments || []).length > 0 || showFiles ? (
            <div>
              <button type="button" onClick={() => setShowFiles((v) => !v)} data-testid={`job-files-toggle-${job.tracking_number}`}
                className="flex items-center gap-1.5 text-xs font-bold text-kilted-green hover:underline">
                <Paperclip className="h-3.5 w-3.5" /> Files &amp; paperwork ({(job.attachments || []).length}) {showFiles ? "— hide" : "— view"}
              </button>
              {showFiles && <div className="mt-2"><OrderAttachments order={job} onChanged={onChange} /></div>}
            </div>
          ) : (
            <button type="button" onClick={() => setShowFiles(true)} data-testid={`job-files-toggle-${job.tracking_number}`}
              className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-kilted-green hover:underline">
              <Paperclip className="h-3.5 w-3.5" /> Attach paperwork
            </button>
          )}
        </div>

        {!completed && (
          <div className="flex flex-wrap gap-2">
            {job.status === "assigned" && !upcoming && <PickupDialog job={job} onChange={onChange} />}
            {(job.status === "picked_up" || job.status === "in_transit") && <OnForwardDialog job={job} onChange={onChange} />}
            {job.status === "picked_up" || job.status === "in_transit" ? <PODDialog job={job} onChange={onChange} /> : null}
            {["assigned", "picked_up", "in_transit"].includes(job.status) && <TransferDialog job={job} onChange={onChange} />}
            {job.status === "paid" && <p className="text-sm text-muted-foreground">Awaiting admin confirmation.</p>}
          </div>
        )}
        {!completed && job.status === "assigned" && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`defer-row-${job.tracking_number}`}>
            <span className="font-medium text-muted-foreground">Busy day? Defer to:</span>
            {[1, 2].map((d) => (
              <button key={d} type="button" onClick={() => defer(d)} disabled={busy}
                data-testid={`defer-${d}-${job.tracking_number}`}
                className="rounded-full border border-kilted-green/40 px-2.5 py-1 font-semibold text-kilted-green transition-colors hover:bg-kilted-green hover:text-white disabled:opacity-50">
                {new Date(Date.now() + d * 864e5).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })}
              </button>
            ))}
            <span className="text-muted-foreground">(2 days max)</span>
          </div>
        )}
        <input ref={photoRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={addPhoto} data-testid={`job-photo-camera-${job.tracking_number}`} />
        <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={addPhoto} data-testid={`job-photo-input-${job.tracking_number}`} />
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => photoRef.current?.click()} data-testid={`job-take-photo-${job.tracking_number}`}
            className="flex items-center gap-1 text-xs font-medium text-kilted-green hover:underline">
            <Camera className="h-3.5 w-3.5" /> Camera
          </button>
          <button type="button" onClick={() => galleryRef.current?.click()} data-testid={`job-add-photo-${job.tracking_number}`}
            className="flex items-center gap-1 text-xs font-medium text-kilted-green hover:underline">
            <ImagePlus className="h-3.5 w-3.5" /> Upload photos
          </button>
          {!completed && ["picked_up", "in_transit", "assigned"].includes(job.status) && (
            <EditOrderDialog order={job} onSaved={onChange} triggerClassName="h-7 gap-1 border-kilted-green px-2 text-xs text-kilted-green hover:bg-kilted-green/10" />
          )}
        </div>
        {completed && job.proof_of_delivery && (
          <p className="flex items-center gap-1.5 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Delivered · {job.proof_of_delivery.atl ? "left under ATL (photo on file)" : `signed by ${job.proof_of_delivery.signature_name}`}</p>
        )}
      </CardContent>
    </Card>
  );
}

function TransferredShell({ job }) {
  const navigate = useNavigate();
  const t = job.transfers?.[job.transfers.length - 1] || {};
  const mine = (job.status_history || []).filter((h) => !t.at || h.timestamp <= t.at).slice(-4);
  return (
    <Card data-testid={`shell-card-${job.tracking_number}`} className="border-dashed bg-secondary/40 opacity-80">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="font-heading text-base text-muted-foreground">
          <button type="button" onClick={() => navigate(`/orders/${job.id}`)} data-testid={`open-job-${job.tracking_number}`} className="flex items-center gap-1 hover:text-kilted-green hover:underline">
            {job.tracking_number} <ExternalLink className="h-3.5 w-3.5 opacity-60" />
          </button>
        </CardTitle>
        <StatusBadge status={job.status} />
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-xs text-muted-foreground">{job.pickup?.city} → {job.dropoff?.city} · {job.size}</p>
        <p className="text-xs font-semibold text-kilted-green" data-testid={`shell-transfer-${job.tracking_number}`}>
          <ArrowRightLeft className="mr-1 inline h-3 w-3" />Transferred to {t.to_name} {t.at ? `· ${new Date(t.at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}{t.note ? ` — ${t.note}` : ""}
        </p>
        <div className="space-y-0.5 border-l-2 border-kilted-gold/40 pl-2">
          {mine.map((h, i) => (
            <p key={i} className="text-[11px] text-muted-foreground">{new Date(h.timestamp).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })} · {h.note || h.status}</p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PODDialog({ job, onChange }) {
  const [open, setOpen] = useState(false);
  const [sig, setSig] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [atlMode, setAtlMode] = useState(false);
  const sigRef = useRef(null);
  const camRef = useRef(null);
  const galRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error(atlMode ? "A photo of where you left the parcel is compulsory for ATL drops" : "Please add a delivery photo"); return; }
    if (!atlMode) {
      if (!sig) { toast.error("Enter recipient signature name"); return; }
      if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Please capture the recipient's signature"); return; }
    }
    setBusy(true);
    try {
      const pos = await getPos();
      const fd = new FormData();
      fd.append("signature_name", atlMode ? "ATL" : sig);
      fd.append("notes", notes);
      fd.append("photo", file);
      if (atlMode) fd.append("atl", "true");
      else fd.append("signature", await sigRef.current.toBlob(), "signature.png");
      if (pos.lat != null) { fd.append("lat", pos.lat); fd.append("lng", pos.lng); }
      await api.post(`/driver/jobs/${job.id}/pod`, fd);
      toast.success(atlMode ? "Delivered under ATL — photo saved as proof." : "Delivered! Proof captured.");
      setOpen(false); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" data-testid={`deliver-button-${job.tracking_number}`} className="h-12 flex-1 gap-2 bg-kilted-gold text-kilted-green hover:bg-kilted-goldLight">
          <PackageCheck className="h-4 w-4" /> Complete delivery
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="pod-dialog">
        <DialogHeader><DialogTitle>Proof of delivery — {job.tracking_number}</DialogTitle><DialogDescription>Capture a photo of the delivered parcel and the recipient's name to complete this drop.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {job.atl === true && (
            <div className={`rounded-md border p-3 ${atlMode ? "border-emerald-400 bg-emerald-50" : "border-kilted-gold/50 bg-kilted-gold/10"}`} data-testid="pod-atl-box">
              <p className="text-xs font-semibold text-kilted-green">Customer gave Authority to Leave (ATL)</p>
              {job.atl_note && <p className="mt-0.5 text-xs text-kilted-green" data-testid="pod-atl-note">Customer asked: leave it <b>{job.atl_note}</b></p>}
              <p className="mt-0.5 text-xs text-muted-foreground">{atlMode ? "Signature block will read \"ATL\". A photo of exactly where you left the parcel is compulsory — it becomes the customer's proof of delivery." : "No one around to sign? You can leave it in a safe place and skip the signature."}</p>
              <Button type="button" size="sm" variant={atlMode ? "default" : "outline"} onClick={() => setAtlMode((v) => !v)} data-testid="pod-atl-toggle"
                className={`mt-2 h-8 gap-1.5 ${atlMode ? "bg-kilted-green hover:bg-kilted-greenLight" : "border-kilted-green text-kilted-green hover:bg-kilted-green/10"}`}>
                <PackageCheck className="h-3.5 w-3.5" /> {atlMode ? "ATL selected — collect a signature instead" : "No one to sign — leave under ATL"}
              </Button>
            </div>
          )}
          {!atlMode && <div className="space-y-1.5"><Label>Recipient name</Label><Input value={sig} onChange={(e) => setSig(e.target.value)} data-testid="pod-signature-input" /></div>}
          {!atlMode && (
            <div className="space-y-1.5">
              <Label>Recipient signature (draw below)</Label>
              <SignaturePad ref={sigRef} />
            </div>
          )}
          {atlMode && <div className="rounded-md border bg-white p-3 text-center" data-testid="pod-atl-signature-block"><p className="text-5xl leading-tight text-kilted-green" style={{ fontFamily: '"Uncial Antiqua", "Outfit", serif' }}>ATL</p><p className="text-[11px] text-muted-foreground">Authority to Leave — no signature collected</p></div>}
          <div className="space-y-1.5"><Label>Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={atlMode ? "e.g. Left behind the front gate, out of sight from the road" : "e.g. Left with reception"} data-testid="pod-notes-input" /></div>
          <div className="space-y-1.5">
            <Label>{atlMode ? "Photo of where you left it (compulsory)" : "Delivery photo (required)"}</Label>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onFile} data-testid="pod-photo-camera-input" />
            <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="pod-photo-input" />
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => camRef.current?.click()} data-testid="pod-take-photo"
                className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                <Camera className="h-4 w-4" /> Take photo
              </Button>
              <Button type="button" variant="outline" onClick={() => galRef.current?.click()} data-testid="pod-upload-photo"
                className="flex-1 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
                <ImagePlus className="h-4 w-4" /> Upload
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Snap a photo at the drop-off, or upload one from your gallery.</p>
            {preview && <img src={preview} alt="Delivery preview" className="mt-2 max-h-40 rounded-md border" data-testid="pod-photo-preview" />}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !file} data-testid="pod-submit-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm delivered
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OnForwardDialog({ job, onChange }) {
  const [open, setOpen] = useState(false);
  const [carrier, setCarrier] = useState("");
  const [reference, setReference] = useState("");
  const [receiver, setReceiver] = useState("");
  const [busy, setBusy] = useState(false);
  const sigRef = useRef(null);

  const submit = async () => {
    if (!carrier.trim()) { toast.error("Enter the carrier name"); return; }
    if (!receiver.trim()) { toast.error("Enter who accepted the parcel at the carrier"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Capture the receiving carrier's signature"); return; }
    setBusy(true);
    try {
      const sigBlob = await sigRef.current.toBlob();
      const pos = await getPos();
      const fd = new FormData();
      fd.append("carrier", carrier);
      fd.append("reference", reference);
      fd.append("signature_name", receiver);
      fd.append("signature", sigBlob, "signature.png");
      if (pos.lat != null) { fd.append("lat", pos.lat); fd.append("lng", pos.lng); }
      await api.post(`/driver/jobs/${job.id}/onforward`, fd);
      toast.success(`On-forwarded to ${carrier} — now tracked manually.`);
      setOpen(false); onChange();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="lg" variant="outline" data-testid={`onforward-button-${job.tracking_number}`} className="h-12 gap-2 border-kilted-green text-kilted-green hover:bg-kilted-green/10">
          <CornerUpRight className="h-4 w-4" /> On-forward
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="onforward-dialog">
        <DialogHeader>
          <DialogTitle>On-forward — {job.tracking_number}</DialogTitle>
          <DialogDescription>Handing this parcel to another carrier. Capture their signature — the order then moves to manual tracking.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>Carrier</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. Australia Post, TNT" data-testid="onforward-carrier-input" /></div>
          <div className="space-y-1.5"><Label>Carrier reference / connote</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Their tracking number (optional)" data-testid="onforward-reference-input" /></div>
          <div className="space-y-1.5"><Label>Accepted by</Label><Input value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="Name of the person who took the parcel" data-testid="onforward-receiver-input" /></div>
          <div className="space-y-1.5"><Label>Their signature (draw below)</Label><SignaturePad ref={sigRef} /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} data-testid="onforward-submit-button" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CornerUpRight className="h-4 w-4" />} Confirm on-forward
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
